import React from "react";
import { Utensils } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { fullMenu } from "@/data/fullMenu";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Reveal from "@/components/Reveal";

const pickLang = (obj, lang) => (obj && (obj[lang] || obj.en)) || "";

const AvailabilityTag = ({ type, lang }) => {
  if (type === "all_day") return null;
  const label = { pt: "ATÉ ÀS 18:00h", en: "UNTIL 18:00h", fr: "JUSQU'À 18h", es: "HASTA 18h" }[lang];
  return (
    <div className="mt-3 inline-block px-3 py-1.5 bg-[var(--ms-warm-bone)] text-[var(--ms-blue-dark)]">
      <span className="text-[9px] font-black tracking-[0.15em] uppercase">{label}</span>
    </div>
  );
};

const Item = ({ item, lang, idx, signatureLabel }) => (
  <article className="py-7 border-b border-[#003366]/10 last:border-0 group">
    <div className="flex flex-wrap items-baseline gap-3">
      <span className="text-[10px] font-bold text-[var(--ms-gold-dark)] tracking-widest tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
      <h4 className="font-serif text-lg text-[var(--ms-blue-dark)] font-light group-hover:text-[var(--ms-blue-dark)] transition-colors">{pickLang(item.name, lang)}</h4>
      {item.signature && <span className="ml-2 text-[8px] tracking-[0.25em] uppercase text-[var(--ms-blue-dark)] border border-[#D4AF37]/40 px-2 py-0.5 rounded-sm">{signatureLabel}</span>}
    </div>
    <div className="pl-[34px]"><AvailabilityTag type={item.availability} lang={lang} /></div>
  </article>
);

export const FullMenu = () => {
  const { lang, t } = useLang();
  const signatureLabel = { en: "Signature", pt: "Especialidade", fr: "Signature", es: "Firma" }[lang] || "Signature";

  return (
    <section id="full-menu" className="section-pad bg-[var(--ms-warm-bone)] text-[var(--ms-ink)]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal className="mb-16">
          <span className="ornament text-[var(--ms-blue-dark)]">{t.fullMenu.overline}</span>
          <h2 className="mt-6 font-serif text-[var(--ms-blue-dark)] text-4xl md:text-6xl font-light tracking-tight leading-[1.1]">{t.fullMenu.title}</h2>
        </Reveal>

        <Reveal delay={150}>
          <Accordion type="single" collapsible className="space-y-4">
            {fullMenu.categories.map((cat) => (
              <AccordionItem key={cat.id} value={cat.id} className="border border-[#003366]/10 bg-[var(--ms-soft-white)] rounded-none overflow-hidden [&[data-state=open]]:border-[#D4AF37]/30 transition-all duration-300 hover:border-[#003366]/15">
                <AccordionTrigger className="px-6 md:px-10 py-7 hover:no-underline group">
                  <div className="flex items-center gap-3 md:gap-6 text-left">
                    <div className="h-12 w-12 shrink-0 flex items-center justify-center rounded-full bg-[var(--ms-ivory)] border border-[#003366]/15 group-hover:border-[#D4AF37]/50 transition-colors">
                      <Utensils className="h-5 w-5 text-[var(--ms-blue-dark)]" />
                    </div>
                    <div className="font-serif text-xl md:text-2xl font-light tracking-wide">{pickLang(cat.label, lang)}</div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 md:px-10 pb-8 border-t border-[#003366]/10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 pt-6">
                    {cat.items.map((it, i) => <Item key={it.id} item={it} lang={lang} idx={i} signatureLabel={signatureLabel} />)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-16 text-center">
            <div className="h-12 w-[1px] bg-gradient-to-b from-[#D4AF37] to-transparent mx-auto mb-6"></div>
            <p className="text-[11px] tracking-[0.1em] text-[var(--ms-blue-dark)] font-medium max-w-2xl mx-auto italic leading-relaxed">{pickLang(fullMenu.footerNote, lang)}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
